/** Legal page copy adapted from https://easebyai.in/ legal pages. */

export function getLegalPages(brandName) {
  const brand = brandName || "Our platform";
  const lastUpdated = "February 24, 2026";

  return {
    "privacy-policy": {
      title: "Privacy Policy",
      lastUpdated,
      sections: [
        {
          heading: "1. Introduction",
          paragraphs: [
            `Welcome to ${brand}. We respect your privacy and are committed to protecting your personal data. This privacy policy explains how we collect, use, and safeguard your information when you use our WhatsApp automation platform.`,
          ],
        },
        {
          heading: "2. Information We Collect",
          paragraphs: ["We collect several types of information:"],
          bullets: [
            "Account information (name, email address, phone number)",
            "WhatsApp Business API credentials and configuration data",
            "Message content and customer interaction data",
            "Usage data and analytics",
            "Payment and billing information",
            "Technical data (IP address, browser type, device information)",
          ],
        },
        {
          heading: "3. How We Use Your Information",
          paragraphs: ["We use your information to:"],
          bullets: [
            "Provide and maintain our WhatsApp automation services",
            "Process your transactions and manage your account",
            "Send you service updates and important notifications",
            "Improve our platform and develop new features",
            "Provide customer support and respond to your inquiries",
            "Detect and prevent fraud or security issues",
            "Comply with legal obligations",
          ],
        },
        {
          heading: "4. Data Security",
          paragraphs: [
            "We implement industry-standard security measures to protect your data, including encryption, secure servers, and regular security audits. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.",
          ],
        },
        {
          heading: "5. Data Sharing and Disclosure",
          paragraphs: ["We may share your information with:"],
          bullets: [
            "Meta (Facebook) for WhatsApp Business API integration",
            "Third-party service providers who assist in our operations",
            "Law enforcement or government authorities when legally required",
            "Business partners with your explicit consent",
          ],
          after: "We never sell your personal data to third parties.",
        },
        {
          heading: "6. Data Retention",
          paragraphs: [
            "We retain your personal data for as long as necessary to provide our services and comply with legal obligations. You may request deletion of your data at any time, subject to certain legal requirements.",
          ],
        },
        {
          heading: "7. Your Rights",
          paragraphs: ["You have the right to:"],
          bullets: [
            "Access your personal data",
            "Correct inaccurate data",
            "Request deletion of your data",
            "Object to processing of your data",
            "Export your data in a portable format",
            "Withdraw consent at any time",
          ],
        },
        {
          heading: "8. Cookies and Tracking",
          paragraphs: [
            "We use cookies and similar tracking technologies to improve your experience on our platform. You can control cookie preferences through your browser settings.",
          ],
        },
        {
          heading: "9. International Data Transfers",
          paragraphs: [
            "Your data may be transferred to and processed in countries outside your residence. We ensure appropriate safeguards are in place to protect your data in accordance with this privacy policy.",
          ],
        },
        {
          heading: "10. Changes to This Policy",
          paragraphs: [
            'We may update this privacy policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last updated" date.',
          ],
        },
        {
          heading: "11. Contact Us",
          paragraphs: [
            "If you have any questions about this privacy policy or our data practices, please contact us through the support channels listed on our website.",
          ],
        },
      ],
    },

    "terms-and-conditions": {
      title: "Terms and Conditions",
      lastUpdated,
      sections: [
        {
          heading: "1. Acceptance of Terms",
          paragraphs: [
            `By accessing and using ${brand}'s WhatsApp automation platform, you accept and agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use our services.`,
          ],
        },
        {
          heading: "2. Service Description",
          paragraphs: [
            `${brand} provides WhatsApp Business API integration, automation, chatbot development, and customer communication management tools. We reserve the right to modify, suspend, or discontinue any aspect of our services at any time.`,
          ],
        },
        {
          heading: "3. Account Registration",
          paragraphs: ["To use our services, you must:"],
          bullets: [
            "Provide accurate and complete registration information",
            "Maintain the security of your account credentials",
            "Be at least 18 years old or have legal capacity to enter contracts",
            "Have authority to bind your organization to these terms",
            "Notify us immediately of any unauthorized account access",
          ],
        },
        {
          heading: "4. WhatsApp Business API Compliance",
          paragraphs: ["You agree to comply with:"],
          bullets: [
            "WhatsApp Business API Terms of Service",
            "Meta's Business Terms and Commerce Policies",
            "All applicable laws and regulations regarding messaging",
            "Anti-spam and data protection regulations",
          ],
        },
        {
          heading: "5. Acceptable Use Policy",
          paragraphs: ["You may not use our services to:"],
          bullets: [
            "Send spam or unsolicited messages",
            "Violate any laws or regulations",
            "Infringe intellectual property rights",
            "Transmit malware or harmful code",
            "Harass, abuse, or harm others",
            "Engage in fraudulent activities",
            "Attempt to breach our security measures",
            "Resell our services without authorization",
          ],
        },
        {
          heading: "6. Pricing and Payments",
          paragraphs: [
            "Subscription fees are billed according to your selected plan. You agree to pay all applicable fees and charges. We reserve the right to modify pricing with 30 days notice. WhatsApp API charges from Meta are separate and billed according to Meta's pricing.",
          ],
        },
        {
          heading: "7. Data Ownership and Usage",
          paragraphs: [
            "You retain ownership of your data and content. By using our services, you grant us a license to process and store your data to provide our services. We may use anonymized, aggregated data for analytics and service improvement.",
          ],
        },
        {
          heading: "8. Service Level and Uptime",
          paragraphs: [
            "While we strive for high availability, we do not guarantee uninterrupted service. We are not liable for service interruptions due to maintenance, updates, or circumstances beyond our control.",
          ],
        },
        {
          heading: "9. Intellectual Property",
          paragraphs: [
            `All content, features, and functionality of our platform are owned by ${brand} and protected by intellectual property laws. You may not copy, modify, distribute, or reverse engineer our services without permission.`,
          ],
        },
        {
          heading: "10. Limitation of Liability",
          paragraphs: [
            `To the maximum extent permitted by law, ${brand} shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of our services. Our total liability shall not exceed the amount you paid us in the 12 months preceding the claim.`,
          ],
        },
        {
          heading: "11. Indemnification",
          paragraphs: [
            `You agree to indemnify and hold ${brand} harmless from any claims, damages, or expenses arising from your use of our services, violation of these terms, or infringement of any rights of others.`,
          ],
        },
        {
          heading: "12. Termination",
          paragraphs: [
            "We may suspend or terminate your account at any time for violation of these terms. You may cancel your subscription at any time. Upon termination, you will lose access to your account and data, subject to our data retention policies.",
          ],
        },
        {
          heading: "13. Modifications to Terms",
          paragraphs: [
            "We reserve the right to modify these terms at any time. We will notify you of material changes via email or platform notification. Continued use of our services after changes constitutes acceptance of the new terms.",
          ],
        },
        {
          heading: "14. Governing Law",
          paragraphs: [
            "These terms are governed by the laws of India. Any disputes shall be resolved in the courts of India.",
          ],
        },
        {
          heading: "15. Contact Information",
          paragraphs: [
            "For questions about these Terms and Conditions, please contact us through the support channels listed on our website.",
          ],
        },
        {
          heading: "16. Entire Agreement",
          paragraphs: [
            `These Terms and Conditions, together with our Privacy Policy, constitute the entire agreement between you and ${brand} regarding the use of our services.`,
          ],
        },
      ],
    },

    "refund-policy": {
      title: "Refund and Cancellation Policy",
      lastUpdated,
      sections: [
        {
          heading: "Overview",
          paragraphs: [
            `At ${brand}, we are committed to transparency regarding our refund and cancellation policies. Please read this policy carefully before subscribing to our services.`,
          ],
        },
        {
          heading: "No Refund Policy",
          notice:
            "All payments made for subscription plans, services, or features are final and non-refundable. We do not provide refunds under any circumstances, including but not limited to partial usage, early termination, dissatisfaction with services, or technical issues.",
          paragraphs: ["This no-refund policy applies to:"],
          bullets: [
            "Monthly and annual subscription plans",
            "One-time setup fees or charges",
            "Additional feature purchases or upgrades",
            "WhatsApp API usage charges",
            "Any other payments made through our platform",
          ],
        },
        {
          heading: "Account Cancellation",
          paragraphs: [
            "You have the right to cancel your subscription at any time. However, please note the following:",
          ],
          bullets: [
            "Cancellation requests can be submitted through your account dashboard or by contacting our support team",
            "Upon cancellation, you will retain access to your subscription benefits until the end of your current billing period",
            "No refunds will be issued for the remaining time in your billing period",
            "Your account will not be automatically renewed after the current period ends",
            "Cancellation does not entitle you to a prorated refund or credit",
          ],
        },
        {
          heading: "Account Deletion",
          paragraphs: ["If you wish to permanently delete your account:"],
          bullets: [
            "You can request account deletion through your account settings or by contacting support",
            "Account deletion is permanent and cannot be undone",
            "All your data, including messages, contacts, and automation workflows, will be permanently deleted",
            "Account deletion does not result in any refund of paid amounts",
            "Any remaining subscription time will be forfeited upon account deletion",
            "We will process deletion requests within 30 days, as required by data protection regulations",
          ],
        },
        {
          heading: "Free Trial Considerations",
          paragraphs: ["If we offer a free trial period:"],
          bullets: [
            "You can cancel during the trial period to avoid being charged",
            "Cancellation must be completed before the trial period ends",
            "Once a trial converts to a paid subscription, the no-refund policy applies",
            "We recommend thoroughly testing our platform during the trial period",
          ],
        },
        {
          heading: "Billing Issues and Payment Disputes",
          paragraphs: [
            "If you believe there has been a billing error or unauthorized charge:",
          ],
          bullets: [
            "Contact our support team immediately with details of the issue",
            "We will investigate legitimate billing errors and make corrections if warranted",
            "Unauthorized charges may be reversed upon verification",
            "You must report billing issues within 30 days of the charge",
          ],
          after:
            "Please note that dissatisfaction with service, change of mind, or underutilization of features do not constitute billing errors and will not result in refunds.",
        },
        {
          heading: "Service Interruptions",
          paragraphs: [
            `In the rare event of extended service outages or disruptions caused by ${brand}:`,
          ],
          bullets: [
            "We will work diligently to restore services as quickly as possible",
            "We may, at our sole discretion, offer service credits or extensions",
            "Service credits do not constitute cash refunds and can only be applied to future subscriptions",
            "Interruptions caused by third parties (including WhatsApp/Meta) or circumstances beyond our control do not qualify for refunds or credits",
          ],
        },
        {
          heading: "How to Cancel Your Subscription",
          paragraphs: ["To cancel your subscription:"],
          ordered: [
            `Log into your ${brand} account`,
            "Navigate to Account Settings or Billing",
            'Select "Cancel Subscription" option',
            "Follow the prompts to confirm cancellation",
            "You will receive a confirmation email once cancellation is processed",
          ],
          after:
            "Alternatively, you can contact our support team for assistance with cancellation.",
        },
        {
          heading: "Recommendation",
          paragraphs: ["We strongly recommend that you:"],
          bullets: [
            "Carefully review all plan features and pricing before subscribing",
            "Take advantage of any free trial period to fully evaluate our platform",
            "Contact our support team with any questions before making a purchase",
            "Understand that all sales are final before completing payment",
          ],
        },
        {
          heading: "Policy Changes",
          paragraphs: [
            `${brand} reserves the right to modify this Refund and Cancellation Policy at any time. Changes will be effective immediately upon posting to our website. Your continued use of our services after policy changes constitutes acceptance of the updated policy.`,
          ],
        },
        {
          heading: "Contact Us",
          paragraphs: [
            `If you have questions about this Refund and Cancellation Policy, please contact us through the support channels listed on our website.`,
            `By subscribing to ${brand} services, you acknowledge that you have read, understood, and agree to this Refund and Cancellation Policy.`,
          ],
        },
      ],
    },

    security: {
      title: "Security",
      lastUpdated,
      sections: [
        {
          heading: "Our commitment",
          paragraphs: [
            `We maintain enterprise-grade security standards with a 99.9% uptime target. All customer data is encrypted and stored securely. Compliance requirements may vary by industry; contact us if you need specific certifications or documentation.`,
          ],
        },
        {
          heading: "Data protection measures",
          paragraphs: [
            "We implement industry-standard security measures to protect your data, including encryption, secure servers, and regular security audits.",
          ],
          bullets: [
            "Encrypted data in transit (HTTPS / TLS)",
            "Secure storage of WhatsApp Business API credentials and tokens",
            "Access controls for team members and agents",
            "Monitoring to detect and prevent fraud or abuse",
          ],
        },
        {
          heading: "WhatsApp / Meta integration",
          paragraphs: [
            "WhatsApp messaging is delivered through Meta's Cloud API. Business credentials and message traffic are handled according to Meta's platform policies and our Privacy Policy.",
          ],
        },
        {
          heading: "Your responsibilities",
          paragraphs: [
            "You are responsible for keeping account credentials secure, managing team access, and complying with WhatsApp Business and applicable privacy laws for your customers' data.",
          ],
        },
        {
          heading: "More information",
          paragraphs: [
            "For details on how we collect and use personal data, see our Privacy Policy. For questions about security practices, contact support through the channels listed on our website.",
          ],
        },
      ],
    },
  };
}

export const LEGAL_SLUGS = [
  "privacy-policy",
  "terms-and-conditions",
  "refund-policy",
  "security",
];
